import { useCallback, useRef, useState } from "react";

/**
 * Wraps an async handler and reports whether it is in flight. Re-entry is ignored, so a double
 * click cannot fire the same mutation twice — which for an endpoint that spends money matters
 * more than it usually would.
 *
 * Errors propagate to the caller: they are surfaced where the action lives, not swallowed here.
 */
export function useAsyncAction<TArgs extends unknown[]>(
  action: (...args: TArgs) => Promise<unknown>,
): [(...args: TArgs) => Promise<void>, boolean] {
  const [busy, setBusy] = useState(false);
  const running = useRef(false);

  const run = useCallback(
    async (...args: TArgs) => {
      if (running.current) return;
      running.current = true;
      setBusy(true);
      try {
        await action(...args);
      } finally {
        running.current = false;
        setBusy(false);
      }
    },
    [action],
  );

  return [run, busy];
}
